import { FunctionComponent, SVGProps } from "react";
import colors from "tailwindcss/colors";

import { EntityColorSchema, EntityIconToken } from "../api";
import IconAffiliate from "../assets/icons/affiliate.svg";
import IconAffiliateRaw from "../assets/icons/affiliate.svg?raw";
import IconBitcoin from "../assets/icons/bitcoin.svg";
import IconBitcoinRaw from "../assets/icons/bitcoin.svg?raw";
import IconBriefcase from "../assets/icons/briefcase.svg";
import IconBriefcaseRaw from "../assets/icons/briefcase.svg?raw";
import IconBuildingBank from "../assets/icons/building-bank.svg";
import IconBuildingBankRaw from "../assets/icons/building-bank.svg?raw";
import IconBuildings from "../assets/icons/buildings.svg";
import IconBuildingsRaw from "../assets/icons/buildings.svg?raw";
import IconCodeDots from "../assets/icons/code-dots.svg";
import IconCodeDotsRaw from "../assets/icons/code-dots.svg?raw";
import IconDeviceMobile from "../assets/icons/device-mobile.svg";
import IconDeviceMobileRaw from "../assets/icons/device-mobile.svg?raw";
import IconFile from "../assets/icons/file.svg";
import IconFileRaw from "../assets/icons/file.svg?raw";
import IconLink from "../assets/icons/link.svg";
import IconLinkRaw from "../assets/icons/link.svg?raw";
import IconMail from "../assets/icons/mail.svg";
import IconMaiRaw from "../assets/icons/mail.svg?raw";
import IconMapPin from "../assets/icons/map-pin.svg";
import IconMapPinRaw from "../assets/icons/map-pin.svg?raw";
import IconRadar from "../assets/icons/radar.svg";
import IconRadarRaw from "../assets/icons/radar.svg?raw";
import IconServer from "../assets/icons/server.svg";
import IconServerRaw from "../assets/icons/server.svg?raw";
import IconServerBolt from "../assets/icons/server-bolt.svg";
import IconServerBoltRaw from "../assets/icons/server-bolt.svg?raw";
import IconServerCog from "../assets/icons/server-cog.svg";
import IconServerCogRaw from "../assets/icons/server-cog.svg?raw";
import IconShieldCheck from "../assets/icons/shield-check.svg";
import IconShieldCheckRaw from "../assets/icons/shield-check.svg?raw";
import IconUser from "../assets/icons/user.svg";
import IconUserRaw from "../assets/icons/user.svg?raw";
import IconUsersGroup from "../assets/icons/users-group.svg";
import IconUsersGroupRaw from "../assets/icons/users-group.svg?raw";
import IconWorldWww from "../assets/icons/world-www.svg";
import IconWorldWwwRaw from "../assets/icons/world-www.svg?raw";

export type EntityStyleColors = {
  background: string;
  border: string;
  selected: string;
  text: string;
};

export const colorSchemas: Record<EntityColorSchema, EntityStyleColors> = {
  [EntityColorSchema.AMBER]: {
    background: colors.amber[700],
    border: colors.amber[600],
    selected: colors.amber[400],
    text: colors.amber[500],
  },
  [EntityColorSchema.BLUE]: {
    background: colors.blue[700],
    border: colors.blue[600],
    selected: colors.blue[400],
    text: colors.blue[500],
  },
  [EntityColorSchema.CYAN]: {
    background: colors.cyan[700],
    border: colors.cyan[600],
    selected: colors.cyan[400],
    text: colors.cyan[500],
  },
  [EntityColorSchema.EMERALD]: {
    background: colors.emerald[700],
    border: colors.emerald[600],
    selected: colors.emerald[400],
    text: colors.emerald[500],
  },
  [EntityColorSchema.FUCHSIA]: {
    background: colors.fuchsia[700],
    border: colors.fuchsia[600],
    selected: colors.fuchsia[400],
    text: colors.fuchsia[500],
  },
  [EntityColorSchema.GREEN]: {
    background: colors.green[700],
    border: colors.green[600],
    selected: colors.green[400],
    text: colors.green[500],
  },
  [EntityColorSchema.INDIGO]: {
    background: colors.indigo[700],
    border: colors.indigo[600],
    selected: colors.indigo[400],
    text: colors.indigo[500],
  },
  [EntityColorSchema.LIME]: {
    background: colors.lime[700],
    border: colors.lime[600],
    selected: colors.lime[400],
    text: colors.lime[500],
  },
  [EntityColorSchema.NEUTRAL]: {
    background: colors.neutral[700],
    border: colors.neutral[600],
    selected: colors.neutral[400],
    text: colors.neutral[500],
  },
  [EntityColorSchema.ORANGE]: {
    background: colors.orange[700],
    border: colors.orange[600],
    selected: colors.orange[400],
    text: colors.orange[500],
  },
  [EntityColorSchema.PINK]: {
    background: colors.pink[700],
    border: colors.pink[600],
    selected: colors.pink[400],
    text: colors.pink[500],
  },
  [EntityColorSchema.PURPLE]: {
    background: colors.purple[700],
    border: colors.purple[600],
    selected: colors.purple[400],
    text: colors.purple[500],
  },
  [EntityColorSchema.RED]: {
    background: colors.red[700],
    border: colors.red[600],
    selected: colors.red[400],
    text: colors.red[500],
  },
  [EntityColorSchema.ROSE]: {
    background: colors.rose[700],
    border: colors.rose[600],
    selected: colors.rose[400],
    text: colors.rose[500],
  },
  [EntityColorSchema.SKY]: {
    background: colors.sky[700],
    border: colors.sky[600],
    selected: colors.sky[400],
    text: colors.sky[500],
  },
  [EntityColorSchema.TEAL]: {
    background: colors.teal[700],
    border: colors.teal[600],
    selected: colors.teal[400],
    text: colors.teal[500],
  },
  [EntityColorSchema.VIOLET]: {
    background: colors.violet[700],
    border: colors.violet[600],
    selected: colors.violet[400],
    text: colors.violet[500],
  },
  [EntityColorSchema.YELLOW]: {
    background: colors.yellow[700],
    border: colors.yellow[600],
    selected: colors.yellow[400],
    text: colors.yellow[500],
  },
} as const;

export const iconsSchemas: Record<
  EntityIconToken,
  {
    // We want to have both the component and the raw SVG string because we need to use the raw SVG string in the editor
    component: FunctionComponent<SVGProps<SVGSVGElement>>;
    raw: string;
  }
> = {
  [EntityIconToken.AFFILIATE]: {
    component: IconAffiliate,
    raw: IconAffiliateRaw,
  },
  [EntityIconToken.BITCOIN]: {
    component: IconBitcoin,
    raw: IconBitcoinRaw,
  },
  [EntityIconToken.BRIEFCASE]: {
    component: IconBriefcase,
    raw: IconBriefcaseRaw,
  },
  [EntityIconToken.BUILDING_BANK]: {
    component: IconBuildingBank,
    raw: IconBuildingBankRaw,
  },
  [EntityIconToken.BUILDINGS]: {
    component: IconBuildings,
    raw: IconBuildingsRaw,
  },
  [EntityIconToken.CODE_DOTS]: {
    component: IconCodeDots,
    raw: IconCodeDotsRaw,
  },
  [EntityIconToken.DEVICE_MOBILE]: {
    component: IconDeviceMobile,
    raw: IconDeviceMobileRaw,
  },
  [EntityIconToken.FILE]: {
    component: IconFile,
    raw: IconFileRaw,
  },
  [EntityIconToken.LINK]: {
    component: IconLink,
    raw: IconLinkRaw,
  },
  [EntityIconToken.MAIL]: {
    component: IconMail,
    raw: IconMaiRaw,
  },
  [EntityIconToken.MAP_PIN]: {
    component: IconMapPin,
    raw: IconMapPinRaw,
  },
  [EntityIconToken.RADAR]: {
    component: IconRadar,
    raw: IconRadarRaw,
  },
  [EntityIconToken.SERVER]: {
    component: IconServer,
    raw: IconServerRaw,
  },
  [EntityIconToken.SERVER_BOLT]: {
    component: IconServerBolt,
    raw: IconServerBoltRaw,
  },
  [EntityIconToken.SERVER_COG]: {
    component: IconServerCog,
    raw: IconServerCogRaw,
  },
  [EntityIconToken.SHIELD_CHECK]: {
    component: IconShieldCheck,
    raw: IconShieldCheckRaw,
  },
  [EntityIconToken.USER]: {
    component: IconUser,
    raw: IconUserRaw,
  },
  [EntityIconToken.USERS_GROUP]: {
    component: IconUsersGroup,
    raw: IconUsersGroupRaw,
  },
  [EntityIconToken.WORLD_WWW]: {
    component: IconWorldWww,
    raw: IconWorldWwwRaw,
  },
};
